import React, {
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import PropTypes from 'prop-types'
import { Spinner } from '@blueprintjs/core/lib/cjs/components/spinner/spinner'
import { InputGroup } from '@blueprintjs/core/lib/cjs/components/forms/inputGroup'
import { Button } from '@blueprintjs/core/lib/cjs/components/button/buttons'
import { Checkbox } from '@blueprintjs/core/lib/cjs/components/forms/controls'
import { Menu } from '@blueprintjs/core/lib/cjs/components/menu/menu'
import { MenuItem } from '@blueprintjs/core/lib/cjs/components/menu/menuItem'
import cx from 'classnames'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical'
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons/faCirclePlus'
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons/faEllipsisVertical'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown'
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp'
import { faDiagramNested } from '@fortawesome/pro-regular-svg-icons/faDiagramNested'
import DataPlaceholder from 'src/components/DataPlaceholder'
import CreateActionModal from 'src/components/Template/CreateActionModal'
import DeleteTemplateModal from 'src/components/Template/DeleteTemplateModal'
import RenameActionModal from 'src/components/Template/RenameActionModal'
import IconMenu from 'src/components/IconMenu'
import { UserDataContext } from 'src/HOCs/withAuth'
import {
    RUNBOOKS_TEMPLATES_MAINTAIN,
    isAllowed,
} from 'src/components/auth/permissions'
import noRunbooksImage from 'src/assets/icons/no-runbooks.svg'
import { DELETE_MODE, ROW_SIZE, ROW_PLACEHOLDER_OFFSET } from './constants'
import {
    appendAsChild,
    buildActionByIdMap,
    cloneActionTree,
    getParentActionIds,
    getVisibleRows,
    getSiblingRange,
    insertAtParent,
    insertAtTopLevel,
    removeActionById,
    resequenceActions,
} from './utils'
import styles from './templateActionList.module.scss'

/**
 * Component to display a list of actions for a runbook template, with support for searching, selecting, expanding/collapsing, and drag-and-drop reordering.
 * @param {Object} props
 * @param {Array<Object>} props.actions - The list of actions to display.
 * @param {boolean} props.isLoading - Whether the actions are currently loading.
 * @param {Function} props.onActionsChange - Callback function to handle changes to the actions.
 * @param {number} props.templateId - The ID of the current template.
 * @returns {JSX.Element} The rendered component.
 */
function TemplateActionsList({
    actions,
    isLoading,
    isAddingActions = false,
    disableAddAction = false,
    onActionsChange,
    onAddActions = () => {},
    onQueueDeleteAction,
    templateId,
    resetExpandVersion = 0,
}) {
    const userData = useContext(UserDataContext)
    const canRenameAction = isAllowed(userData, RUNBOOKS_TEMPLATES_MAINTAIN)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedActionIds, setSelectedActionIds] = useState([])
    const [expandedActionIds, setExpandedActionIds] = useState(() =>
        getParentActionIds(actions)
    )
    const [currentMatchIndex, setCurrentMatchIndex] = useState(null)
    const [focusedActionId, setFocusedActionId] = useState(null)
    const [combineActionId, setCombineActionId] = useState(null)
    const [isAddActionDialogOpen, setIsAddActionDialogOpen] = useState(false)
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        mode: DELETE_MODE.SINGLE,
        action: null,
        ids: [],
    })
    const [renameDialog, setRenameDialog] = useState({
        open: false,
        actionId: null,
        name: '',
    })
    const previousTemplateId = useRef(templateId)
    const hasInitializedExpansionForTemplate = useRef(false)
    const actionRowRefs = useRef(new Map())
    const normalizedSearchQuery = searchQuery.trim().toLowerCase()

    useEffect(() => {
        if (previousTemplateId.current !== templateId) {
            setSearchQuery('')
            setSelectedActionIds([])
            setExpandedActionIds([])
            setCurrentMatchIndex(null)
            setFocusedActionId(null)
            setCombineActionId(null)
            setIsAddActionDialogOpen(false)
            setRenameDialog({
                open: false,
                actionId: null,
                name: '',
            })
            setDeleteDialog({
                open: false,
                mode: DELETE_MODE.SINGLE,
                action: null,
                ids: [],
            })
            hasInitializedExpansionForTemplate.current = false
            actionRowRefs.current.clear()
        }

        previousTemplateId.current = templateId
    }, [templateId])

    useEffect(() => {
        if (hasInitializedExpansionForTemplate.current || isLoading) {
            return
        }

        setExpandedActionIds(getParentActionIds(actions))
        hasInitializedExpansionForTemplate.current = true
    }, [actions, isLoading])

    useEffect(() => {
        if (!resetExpandVersion) {
            return
        }

        setExpandedActionIds(getParentActionIds(actions))
    }, [actions, resetExpandVersion])

    const visibleRows = useMemo(() => {
        return getVisibleRows({
            actions,
            expandedActionIds,
        })
    }, [actions, expandedActionIds])

    const actionById = useMemo(() => buildActionByIdMap(actions), [actions])
    const disableDragAndDrop = false

    const matchedRows = useMemo(() => {
        if (!normalizedSearchQuery) {
            return []
        }

        return visibleRows.reduce((acc, row, rowIndex) => {
            const action = actionById.get(row.id)
            const actionName = String(action?.name || '').toLowerCase()

            if (actionName.includes(normalizedSearchQuery)) {
                acc.push({
                    rowIndex,
                    actionId: row.id,
                })
            }

            return acc
        }, [])
    }, [actionById, normalizedSearchQuery, visibleRows])

    const currentMatchedActionId =
        currentMatchIndex !== null
            ? matchedRows[currentMatchIndex]?.actionId ?? null
            : null
    const matchedActionIds = useMemo(
        () => new Set(matchedRows.map((match) => match.actionId)),
        [matchedRows]
    )

    useEffect(() => {
        if (!normalizedSearchQuery || !matchedRows.length) {
            setCurrentMatchIndex(null)
            return
        }

        setCurrentMatchIndex((previousMatchIndex) => {
            if (
                previousMatchIndex === null ||
                previousMatchIndex >= matchedRows.length
            ) {
                return 0
            }

            return previousMatchIndex
        })
    }, [normalizedSearchQuery, matchedRows])

    useEffect(() => {
        if (currentMatchIndex === null || !matchedRows.length) {
            return
        }

        const currentMatch = matchedRows[currentMatchIndex]
        if (!currentMatch) {
            return
        }

        setFocusedActionId(currentMatch.actionId)

        const matchedRowElement = actionRowRefs.current.get(
            currentMatch.actionId
        )
        if (matchedRowElement?.scrollIntoView) {
            matchedRowElement.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            })
        }
    }, [currentMatchIndex, matchedRows])

    const goToNextMatch = useCallback(() => {
        if (!matchedRows.length) {
            return
        }

        setCurrentMatchIndex((previousMatchIndex) => {
            if (previousMatchIndex === null) {
                return 0
            }

            return (previousMatchIndex + 1) % matchedRows.length
        })
    }, [matchedRows])

    const goToPreviousMatch = useCallback(() => {
        if (!matchedRows.length) {
            return
        }

        setCurrentMatchIndex((previousMatchIndex) => {
            if (previousMatchIndex === null) {
                return matchedRows.length - 1
            }

            return (
                (previousMatchIndex - 1 + matchedRows.length) %
                matchedRows.length
            )
        })
    }, [matchedRows])

    const handleSearchQueryChange = useCallback((event) => {
        setSearchQuery(event.target.value)
    }, [])

    const clearSearchQuery = useCallback(() => {
        setSearchQuery('')
    }, [])

    const renderHighlightedName = useCallback(
        (actionName, actionId) => {
            const value = String(actionName || '')

            if (!normalizedSearchQuery) {
                return value
            }

            return value
        },
        [normalizedSearchQuery]
    )

    const renderSearchResults = useCallback(() => {
        if (!normalizedSearchQuery || !matchedRows.length) {
            return null
        }

        return (
            <div className={styles.queryResultsContainer} role="status">
                <span className={styles.resultsLabel}>Results</span>
                <span className={styles.resultsCount}>
                    {(currentMatchIndex ?? 0) + 1}/{matchedRows.length}
                </span>
                <Button
                    icon="chevron-up"
                    minimal
                    aria-label="Previous search match"
                    onClick={goToPreviousMatch}
                />
                <Button
                    icon="chevron-down"
                    minimal
                    aria-label="Next search match"
                    onClick={goToNextMatch}
                />
            </div>
        )
    }, [
        currentMatchIndex,
        goToNextMatch,
        goToPreviousMatch,
        matchedRows,
        normalizedSearchQuery,
    ])

    const handleDragStart = useCallback((draggableItem) => {
        const actionRowPlaceholder = document.getElementById(
            'template-action-row-placeholder'
        )

        if (actionRowPlaceholder) {
            actionRowPlaceholder.style.display = 'block'
            actionRowPlaceholder.style.top = `${
                draggableItem.source.index * ROW_SIZE + ROW_PLACEHOLDER_OFFSET
            }px`
        }
    }, [])

    const handleDragUpdate = useCallback((update) => {
        const { combine, destination } = update
        const actionRowPlaceholder = document.getElementById(
            'template-action-row-placeholder'
        )

        if (destination) {
            if (actionRowPlaceholder) {
                actionRowPlaceholder.style.display = 'block'
                actionRowPlaceholder.style.top = `${
                    destination.index * ROW_SIZE + ROW_PLACEHOLDER_OFFSET
                }px`
            }
        } else if (actionRowPlaceholder) {
            actionRowPlaceholder.style.display = 'none'
        }

        setCombineActionId(combine ? Number(combine.draggableId) : null)
    }, [])

    const handleDragEnd = useCallback(
        (result) => {
            const { source, destination, combine } = result
            const actionRowPlaceholder = document.getElementById(
                'template-action-row-placeholder'
            )

            if (actionRowPlaceholder) {
                actionRowPlaceholder.style.display = 'none'
            }

            setCombineActionId(null)

            if (!destination && !combine) {
                return
            }

            const sourceRow = visibleRows[source.index]
            if (!sourceRow) {
                return
            }

            let nextActions = cloneActionTree(actions)
            const removalResult = removeActionById(nextActions, sourceRow.id)

            if (!removalResult.removedAction) {
                return
            }

            nextActions = removalResult.nextActions

            if (destination) {
                if (destination.index === source.index) {
                    return
                }

                const rowsWithoutSource = visibleRows.filter(
                    (_, index) => index !== source.index
                )

                // If dropping within the visual band of siblings, reorder under same parent.
                const sourceParentId = sourceRow.parentId
                const siblingRange = getSiblingRange(
                    rowsWithoutSource,
                    sourceParentId
                )
                const isSiblingReorder =
                    sourceParentId !== null &&
                    siblingRange &&
                    destination.index >= siblingRange.start &&
                    destination.index <= siblingRange.end + 1

                if (isSiblingReorder) {
                    const siblingInsertIndex = rowsWithoutSource
                        .slice(0, destination.index)
                        .filter((row) => row.parentId === sourceParentId).length

                    nextActions = insertAtParent(
                        nextActions,
                        sourceParentId,
                        removalResult.removedAction,
                        siblingInsertIndex
                    )
                } else {
                    nextActions = insertAtTopLevel(
                        nextActions,
                        removalResult.removedAction,
                        destination.index,
                        rowsWithoutSource
                    )
                }

                onActionsChange(resequenceActions(nextActions))
                return
            }

            if (combine) {
                const nextCombineActionId = Number(combine.draggableId)

                if (sourceRow.id === nextCombineActionId) {
                    return
                }

                nextActions = appendAsChild(
                    nextActions,
                    nextCombineActionId,
                    removalResult.removedAction
                )

                setExpandedActionIds((previousExpandedActionIds) =>
                    previousExpandedActionIds.includes(nextCombineActionId)
                        ? previousExpandedActionIds
                        : [...previousExpandedActionIds, nextCombineActionId]
                )

                onActionsChange(resequenceActions(nextActions))
            }
        },
        [actions, onActionsChange, visibleRows]
    )

    const handleToggleActionSelection = useCallback((actionId) => {
        setSelectedActionIds((previousSelectedActionIds) =>
            previousSelectedActionIds.includes(actionId)
                ? previousSelectedActionIds.filter((id) => id !== actionId)
                : [...previousSelectedActionIds, actionId]
        )
    }, [])

    const handleToggleExpand = useCallback((actionId) => {
        setExpandedActionIds((previousExpandedActionIds) =>
            previousExpandedActionIds.includes(actionId)
                ? previousExpandedActionIds.filter((id) => id !== actionId)
                : [...previousExpandedActionIds, actionId]
        )
    }, [])

    const handleOpenDeleteConfirm = useCallback((actionRecord) => {
        setDeleteDialog({
            open: true,
            mode: DELETE_MODE.SINGLE,
            action: actionRecord,
            ids: [],
        })
    }, [])

    const handleCancelDeleteConfirm = useCallback(() => {
        setDeleteDialog({
            open: false,
            mode: DELETE_MODE.SINGLE,
            action: null,
            ids: [],
        })
    }, [])

    const handleOpenBulkDelete = useCallback(() => {
        setDeleteDialog({
            open: true,
            mode: DELETE_MODE.BULK,
            action: null,
            ids: selectedActionIds,
        })
    }, [selectedActionIds])

    const handleConfirmDeleteAction = useCallback(() => {
        const actionId = deleteDialog.action?.id

        if (!actionId) {
            handleCancelDeleteConfirm()
            return
        }

        const removalResult = removeActionById(actions, actionId)

        if (!removalResult.removedAction) {
            handleCancelDeleteConfirm()
            return
        }

        const nextActions = resequenceActions(removalResult.nextActions)

        onActionsChange(nextActions)
        onQueueDeleteAction(actionId)

        setSelectedActionIds((previousSelectedActionIds) =>
            previousSelectedActionIds.filter((id) => id !== actionId)
        )
        setExpandedActionIds((previousExpandedActionIds) =>
            previousExpandedActionIds.filter((id) => id !== actionId)
        )
        setFocusedActionId((previousFocusedActionId) =>
            previousFocusedActionId === actionId
                ? null
                : previousFocusedActionId
        )
        setDeleteDialog({
            open: false,
            mode: DELETE_MODE.SINGLE,
            action: null,
            ids: [],
        })
    }, [
        deleteDialog.action,
        actions,
        onActionsChange,
        onQueueDeleteAction,
        handleCancelDeleteConfirm,
    ])
    const handleOpenRenameDialog = useCallback((actionRecord) => {
        setRenameDialog({
            open: true,
            actionId: actionRecord.id,
            name: actionRecord.name || '',
        })
    }, [])

    const handleCancelRenameDialog = useCallback(() => {
        setRenameDialog({
            open: false,
            actionId: null,
            name: '',
        })
    }, [])

    const handleConfirmRenameAction = useCallback(() => {
        const actionId = renameDialog.actionId
        const nextName = renameDialog.name.trim()

        if (!actionId || !nextName) {
            return
        }

        const renameActionInTree = (actionItems) =>
            actionItems.map((actionItem) => {
                const nextChildren = renameActionInTree(
                    actionItem.children || []
                )

                if (actionItem.id === actionId) {
                    return {
                        ...actionItem,
                        name: nextName,
                        children: nextChildren,
                    }
                }

                return {
                    ...actionItem,
                    children: nextChildren,
                }
            })

        onActionsChange(renameActionInTree(actions))
        handleCancelRenameDialog()
    }, [
        actions,
        handleCancelRenameDialog,
        onActionsChange,
        renameDialog.actionId,
        renameDialog.name,
    ])

    const handleConfirmBulkDelete = useCallback(() => {
        const idsToDelete = deleteDialog.ids

        if (!idsToDelete.length) {
            return
        }

        const removeRecursive = (items) =>
            items
                .filter((item) => !idsToDelete.includes(item.id))
                .map((item) => ({
                    ...item,
                    children: item.children
                        ? removeRecursive(item.children)
                        : [],
                }))

        const nextActions = resequenceActions(removeRecursive(actions))

        onActionsChange(nextActions)

        idsToDelete.forEach((id) => onQueueDeleteAction(id))

        setSelectedActionIds([])
        setDeleteDialog({
            open: false,
            mode: DELETE_MODE.SINGLE,
            action: null,
            ids: [],
        })
    }, [deleteDialog.ids, actions, onActionsChange, onQueueDeleteAction])

    const isBulkDeleteDialog = deleteDialog.mode === 'bulk'
    const deleteWarningMessage = isBulkDeleteDialog
        ? `Are you sure you want to delete ${deleteDialog.ids.length} selected actions?`
        : 'This removes the action from the draft only. The action is permanently deleted only when you click Save template.'
    const handleConfirmDelete = isBulkDeleteDialog
        ? handleConfirmBulkDelete
        : handleConfirmDeleteAction

    const renderActionRow = useCallback(
        (actionRecord, row, dragHandleProps = null, isDragging = false) => {
            const isFocused = focusedActionId === actionRecord.id
            const hasChildren =
                actionRecord.children && actionRecord.children.length > 0
            const isExpanded = expandedActionIds.includes(actionRecord.id)
            const isCombineTarget = combineActionId === actionRecord.id
            const isMatched = matchedActionIds.has(actionRecord.id)
            const isCurrentMatch = actionRecord.id === currentMatchedActionId

            return (
                <React.Fragment key={actionRecord.id}>
                    <div
                        className={cx(styles.actionRow, {
                            [styles.actionRowFocused]:
                                isFocused && !isCurrentMatch,
                            [styles.actionRowMatched]: isMatched,
                            [styles.actionRowCurrentMatch]: isCurrentMatch,
                            [styles.combine]: isCombineTarget,
                            [styles.actionRowDragging]: isDragging,
                        })}
                        ref={(element) =>
                            element
                                ? actionRowRefs.current.set(
                                      actionRecord.id,
                                      element
                                  )
                                : actionRowRefs.current.delete(actionRecord.id)
                        }
                        onClick={() => setFocusedActionId(actionRecord.id)}
                    >
                        <div className={styles.idCell}>
                            {hasChildren && (
                                <button
                                    type="button"
                                    className={styles.expandButton}
                                    aria-expanded={isExpanded}
                                    aria-label={`${
                                        isExpanded ? 'Collapse' : 'Expand'
                                    } child actions for ${actionRecord.name}`}
                                    onClick={(event) => {
                                        event.stopPropagation()
                                        handleToggleExpand(actionRecord.id)
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={
                                            isExpanded
                                                ? faChevronUp
                                                : faChevronDown
                                        }
                                        className={styles.expandIcon}
                                    />
                                </button>
                            )}
                            <span
                                className={styles.sequenceId}
                                title={row.displayIndex}
                            >
                                {row.displayIndex}
                            </span>
                        </div>

                        <div className={styles.dragCell}>
                            <span
                                className={styles.dragHandle}
                                {...dragHandleProps}
                            >
                                <FontAwesomeIcon icon={faGripVertical} />
                            </span>
                        </div>

                        <div className={styles.actionNameCell}>
                            <div className={styles.actionContent}>
                                <Checkbox
                                    checked={selectedActionIds.includes(
                                        actionRecord.id
                                    )}
                                    onChange={() =>
                                        handleToggleActionSelection(
                                            actionRecord.id
                                        )
                                    }
                                    aria-label={`Select action ${actionRecord.name}`}
                                    className={styles.checkbox}
                                />
                                <div
                                    className={styles.actionDetails}
                                    style={{ '--action-depth': row.depth }}
                                >
                                    {row.depth > 0 && (
                                        <FontAwesomeIcon
                                            icon={faDiagramNested}
                                            className={styles.nestedIcon}
                                        />
                                    )}
                                    <span className={styles.actionName}>
                                        {renderHighlightedName(
                                            actionRecord.name,
                                            actionRecord.id
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className={styles.menuCell}>
                                <IconMenu
                                    icon={
                                        <FontAwesomeIcon
                                            icon={faEllipsisVertical}
                                            className={styles.rowIconMenu}
                                        />
                                    }
                                    className={styles.rowMenu}
                                    content={
                                        <Menu>
                                            {canRenameAction && (
                                                <MenuItem
                                                    text="Rename action"
                                                    icon="edit"
                                                    onClick={() =>
                                                        handleOpenRenameDialog(
                                                            actionRecord
                                                        )
                                                    }
                                                />
                                            )}
                                            <MenuItem
                                                text="Delete action"
                                                icon="trash"
                                                onClick={() =>
                                                    handleOpenDeleteConfirm(
                                                        actionRecord
                                                    )
                                                }
                                            />
                                        </Menu>
                                    }
                                />
                            </div>
                            <RenameActionModal
                                isOpen={renameDialog.open}
                                value={renameDialog.name}
                                onChange={(event) =>
                                    setRenameDialog((previousDialog) => ({
                                        ...previousDialog,
                                        name: event.target.value,
                                    }))
                                }
                                onCancel={handleCancelRenameDialog}
                                onConfirm={handleConfirmRenameAction}
                            />
                        </div>
                    </div>
                </React.Fragment>
            )
        },
        [
            combineActionId,
            canRenameAction,
            currentMatchedActionId,
            expandedActionIds,
            focusedActionId,
            handleOpenDeleteConfirm,
            handleCancelRenameDialog,
            handleConfirmRenameAction,
            handleOpenRenameDialog,
            handleToggleActionSelection,
            handleToggleExpand,
            matchedActionIds,
            renameDialog.name,
            renameDialog.open,
            renderHighlightedName,
            selectedActionIds,
        ]
    )

    const renderTableHeader = useCallback(() => {
        return (
            <div className={styles.tableHeader}>
                <div className={styles.headerIdCell}>ID</div>
                <div className={styles.headerDragCell} />
                <div className={styles.headerActionNameCell}>Action name</div>
                <div className={styles.headerMenuCell} />
            </div>
        )
    }, [])

    const handleOpenAddActionDialog = useCallback(() => {
        setIsAddActionDialogOpen(true)
    }, [])

    const handleCloseAddActionDialog = useCallback(() => {
        if (isAddingActions) {
            return
        }

        setIsAddActionDialogOpen(false)
    }, [isAddingActions])

    const handleSubmitDraftActions = useCallback(
        async (nextNames) => {
            if (!nextNames?.length || isAddingActions) {
                return
            }

            await onAddActions(nextNames)
            setIsAddActionDialogOpen(false)
        },
        [isAddingActions, onAddActions]
    )

    return (
        <div className={styles.container}>
            <div className={styles.actionsToolbar}>
                {selectedActionIds.length ? (
                    <div className={styles.selectedCounter}>
                        <span className={styles.selectionCountBadge}>
                            {selectedActionIds.length}
                        </span>
                        <span>actions selected</span>
                    </div>
                ) : (
                    <div />
                )}

                <div className={styles.actionsToolbarButtons}>
                    {selectedActionIds.length > 0 && (
                        <Button
                            icon="trash"
                            intent="danger"
                            onClick={handleOpenBulkDelete}
                            className={styles.deleteActionButton}
                        >
                            Delete action
                        </Button>
                    )}

                    <Button
                        className={styles.addActionButton}
                        icon={<FontAwesomeIcon icon={faCirclePlus} />}
                        onClick={handleOpenAddActionDialog}
                        disabled={
                            disableAddAction || isLoading || isAddingActions
                        }
                    >
                        Add action(s)
                    </Button>
                </div>
            </div>

            <div className={styles.actionsCard}>
                <div className={styles.searchWrapper}>
                    <div className={styles.actionsCardHeader}>
                        {renderSearchResults()}
                        <InputGroup
                            className={styles.actionsSearchInput}
                            aria-label="Search in actions"
                            placeholder="Search in actions"
                            value={searchQuery}
                            onChange={handleSearchQueryChange}
                            leftElement={
                                <FontAwesomeIcon icon={faMagnifyingGlass} />
                            }
                            rightElement={
                                searchQuery && (
                                    <button
                                        type="button"
                                        className={styles.clearSearchButton}
                                        aria-label="Clear search"
                                        onClick={clearSearchQuery}
                                    >
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            className={styles.clearSearchIcon}
                                        />
                                    </button>
                                )
                            }
                        />
                    </div>
                </div>

                {isLoading ? (
                    <div className={styles.actionsLoader}>
                        <Spinner size={Spinner.SIZE_STANDARD} />
                    </div>
                ) : visibleRows.length ? (
                    <>
                        {renderTableHeader()}
                        <DragDropContext
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragUpdate={handleDragUpdate}
                        >
                            <Droppable
                                droppableId="template-actions-droppable"
                                isCombineEnabled={!disableDragAndDrop}
                            >
                                {(provided, snapshot) => (
                                    <div
                                        className={styles.actionsList}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {snapshot.isUsingPlaceholder && (
                                            <div
                                                id="template-action-row-placeholder"
                                                className={
                                                    styles.actionRowPlaceholder
                                                }
                                            />
                                        )}
                                        {visibleRows.map((row, index) => {
                                            const actionRecord = actionById.get(
                                                row.id
                                            )

                                            if (!actionRecord) {
                                                return null
                                            }

                                            return (
                                                <Draggable
                                                    key={row.id}
                                                    draggableId={String(row.id)}
                                                    index={index}
                                                    isDragDisabled={
                                                        disableDragAndDrop
                                                    }
                                                >
                                                    {(
                                                        dragProvided,
                                                        dragSnapshot
                                                    ) => (
                                                        <div
                                                            ref={
                                                                dragProvided.innerRef
                                                            }
                                                            {...dragProvided.draggableProps}
                                                        >
                                                            {renderActionRow(
                                                                actionRecord,
                                                                row,
                                                                dragProvided.dragHandleProps,
                                                                dragSnapshot.isDragging
                                                            )}
                                                        </div>
                                                    )}
                                                </Draggable>
                                            )
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </>
                ) : (
                    <DataPlaceholder
                        title="No actions have been added in this template yet."
                        subtitle={
                            <>
                                Click at the right top corner on{' '}
                                <b>"+ Add action"</b> to start adding them to
                                your template.
                            </>
                        }
                        img={noRunbooksImage}
                    />
                )}
                <CreateActionModal
                    isOpen={isAddActionDialogOpen}
                    isSubmitting={isAddingActions}
                    onClose={handleCloseAddActionDialog}
                    onSubmit={handleSubmitDraftActions}
                />
                <DeleteTemplateModal
                    isOpen={deleteDialog.open}
                    template={deleteDialog.action}
                    mode={deleteDialog.mode}
                    isDeleting={false}
                    title="Delete Action"
                    itemLabel="action"
                    warningMessage={deleteWarningMessage}
                    confirmLabel="Delete"
                    cancelLabel="Cancel"
                    onCancel={handleCancelDeleteConfirm}
                    onConfirm={handleConfirmDelete}
                />
            </div>
        </div>
    )
}

TemplateActionsList.propTypes = {
    actions: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool,
    onActionsChange: PropTypes.func.isRequired,
    onQueueDeleteAction: PropTypes.func,
    templateId: PropTypes.number.isRequired,
    resetExpandVersion: PropTypes.number,
}

export default memo(TemplateActionsList)
